import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import OrganizationsPage from "@/pages/OrganizationsPage";
import LocationsPage from "@/pages/LocationsPage";
import PatientsPage from "@/pages/PatientsPage";
import PatientChartPage from "@/pages/PatientChartPage";
import EncountersPage from "@/pages/EncountersPage";
import EncounterScreenPage from "@/pages/EncounterScreenPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import NewAppointmentPage from "@/pages/NewAppointmentPage";
import UsersPage from "@/pages/UsersPage";
import Icd10CmPage from "@/pages/Icd10CmPage";
import CptPage from "@/pages/CptPage";
import MedicationsPage from "@/pages/MedicationsPage";
import LabsPage from "@/pages/LabsPage";
import RadiologyPage from "@/pages/RadiologyPage";
import PhysicianReferralsPage from "@/pages/PhysicianReferralsPage";
import ReferringProvidersPage from "@/pages/ReferringProvidersPage";
import SmartPhrasesPage from "@/pages/SmartPhrasesPage";
import SmartPhraseFormPage from "@/pages/SmartPhraseFormPage";
import PlaceholderVocabularyPage from "@/pages/PlaceholderVocabularyPage";
import DashboardPage from "@/pages/DashboardPage";
import LoginPage from "@/pages/LoginPage";
import SelectContextPage from "@/pages/SelectContextPage";
import SettingsPage from "@/pages/SettingsPage";
import PxMasterPage from "@/pages/PxMasterPage";
import AllergenMasterPage from "@/pages/AllergenMasterPage";
import SystemChangeHistoryPage from "@/pages/SystemChangeHistoryPage";
import SmsSandboxTestPage from "@/pages/SmsSandboxTestPage";
import AccessControlPage from "@/pages/AccessControlPage";
import GenericReportDesignerPage from "@/pages/GenericReportDesignerPage";
import PageDesignerPage from "@/pages/PageDesignerPage";
import AppShell from "@/components/layout/AppShell";
import { AppearanceProvider } from "@/context/AppearanceContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import {
  canAccessMainTab,
  canWriteMainTab,
  firstAllowedMainPath,
  isMainTabWriteRoute,
  mainTabFromPath,
  writeRouteFallbackPath,
} from "@/lib/maintabAccess";

function Protected({ children, adminOnly }) {
  const { user, isAdmin, context } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!context) return <Navigate to="/select-context" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  const tabId = mainTabFromPath(location.pathname);
  const accessOpts = { isAdmin };
  if (tabId && !canAccessMainTab(user.maintabAccess, tabId, accessOpts)) {
    const fallback = firstAllowedMainPath(user.maintabAccess, accessOpts);
    if (fallback && fallback !== location.pathname) {
      return <Navigate to={fallback} replace />;
    }
    return (
      <AppShell>
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-slate-600">
          You do not have access to this area.
        </div>
      </AppShell>
    );
  }
  if (
    tabId &&
    isMainTabWriteRoute(location.pathname) &&
    !canWriteMainTab(user.maintabAccess, tabId, accessOpts)
  ) {
    const fallback = writeRouteFallbackPath(location.pathname);
    if (fallback !== location.pathname) {
      return <Navigate to={fallback} replace />;
    }
  }
  return <AppShell>{children}</AppShell>;
}

function ContextGate() {
  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <SelectContextPage />;
}

function App() {
  return (
    <AuthProvider>
      <AppearanceProvider>
        <BrowserRouter>
          <Toaster richColors position="bottom-right" />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/select-context" element={<ContextGate />} />
            <Route path="/" element={<Protected><DashboardPage /></Protected>} />
            <Route path="/organizations" element={<Protected><OrganizationsPage /></Protected>} />
            <Route path="/locations" element={<Protected><LocationsPage /></Protected>} />
            <Route path="/patients" element={<Protected><PatientsPage /></Protected>} />
            <Route path="/patients/:patientId" element={<Protected><PatientChartPage /></Protected>} />
            <Route path="/appointments" element={<Protected><AppointmentsPage /></Protected>} />
            <Route path="/appointments/new" element={<Protected><NewAppointmentPage /></Protected>} />
            <Route path="/appointments/:appointmentId/edit" element={<Protected><NewAppointmentPage /></Protected>} />
            <Route path="/encounters" element={<Protected><EncountersPage /></Protected>} />
            <Route path="/encounters/:encounterId" element={<Protected><EncounterScreenPage /></Protected>} />
            <Route path="/icd10-cm" element={<Protected><Icd10CmPage /></Protected>} />
            <Route path="/cpt" element={<Protected><CptPage /></Protected>} />
            <Route path="/medications" element={<Protected><MedicationsPage /></Protected>} />
            <Route path="/labs" element={<Protected><LabsPage /></Protected>} />
            <Route path="/radiology" element={<Protected><RadiologyPage /></Protected>} />
            <Route path="/physician-referrals" element={<Protected><PhysicianReferralsPage /></Protected>} />
            <Route path="/referring-providers" element={<Protected><ReferringProvidersPage /></Protected>} />
            <Route path="/smartphrases" element={<Protected><SmartPhrasesPage /></Protected>} />
            <Route path="/smartphrases/new" element={<Protected><SmartPhraseFormPage /></Protected>} />
            <Route path="/smartphrases/:phraseId/edit" element={<Protected><SmartPhraseFormPage /></Protected>} />
            <Route path="/placeholder-vocabulary" element={<Protected><PlaceholderVocabularyPage /></Protected>} />
            <Route path="/users" element={<Protected adminOnly><UsersPage /></Protected>} />
            <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
            <Route path="/settings/generic-report-designer" element={<Protected><GenericReportDesignerPage /></Protected>} />
            <Route path="/page-designer" element={<Protected><PageDesignerPage /></Protected>} />
            <Route path="/settings/px-master" element={<Protected adminOnly><PxMasterPage /></Protected>} />
            <Route path="/settings/allergen-master" element={<Protected adminOnly><AllergenMasterPage /></Protected>} />
            <Route path="/admin/system-change-history" element={<Protected adminOnly><SystemChangeHistoryPage /></Protected>} />
            <Route path="/admin/sms-sandbox-test" element={<Protected adminOnly><SmsSandboxTestPage /></Protected>} />
            <Route path="/admin/access-control" element={<Protected adminOnly><AccessControlPage /></Protected>} />
          </Routes>
        </BrowserRouter>
      </AppearanceProvider>
    </AuthProvider>
  );
}

export default App;
